import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  BookingLinkFilterParams,
  MeetingBooksLinkRequest,
  MeetingBooksLinkResponse,
  MeetingBookingAvailabilityRequest,
  MeetingBookingAvailabilityResponse,
  MeetingBookingDurationRequest,
  MeetingBookingDurationResponse,
  MeetingBookingFormFieldRequest,
  MeetingBookingFormFieldResponse,
  MeetingBookingMemberRequest,
  MeetingBookingMemberResponse,
  MeetingNurtureEmailRequest,
  MeetingNurtureEmailResponse,
  MeetingReminderConfigRequest,
  MeetingReminderConfigResponse,
  MeetingRoundRobinStateResponse,
} from '../models/meeting.model';

@Injectable({ providedIn: 'root' })
export class MeetingBooksLinkApiService {
  private url = `${environment.apiUrl}api/meeting-books-links`;
  constructor(private http: HttpClient) {}

  // -------------------------- Parent --------------------------

  create(body: MeetingBooksLinkRequest): Observable<MeetingBooksLinkResponse> {
    return this.http.post<MeetingBooksLinkResponse>(this.url, body);
  }
  getById(id: number): Observable<MeetingBooksLinkResponse> {
    return this.http.get<MeetingBooksLinkResponse>(`${this.url}/${id}`);
  }
  update(
    id: number,
    body: MeetingBooksLinkRequest
  ): Observable<MeetingBooksLinkResponse> {
    return this.http.put<MeetingBooksLinkResponse>(`${this.url}/${id}`, body);
  }
  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/${id}`);
  }
  activate(id: number): Observable<MeetingBooksLinkResponse> {
    return this.http.put<MeetingBooksLinkResponse>(
      `${this.url}/${id}/activate`,
      null
    );
  }
  deactivate(id: number): Observable<MeetingBooksLinkResponse> {
    return this.http.put<MeetingBooksLinkResponse>(
      `${this.url}/${id}/deactivate`,
      null
    );
  }
  /** INC 62 — activar/desactivar con body { active }. PATCH (el backend también acepta PUT). */
  setActive(id: number, active: boolean): Observable<MeetingBooksLinkResponse> {
    return this.http.patch<MeetingBooksLinkResponse>(
      `${this.url}/${id}/active`,
      { active }
    );
  }

  listByUser(userId: number): Observable<MeetingBooksLinkResponse[]> {
    return this.http.get<MeetingBooksLinkResponse[]>(
      `${this.url}/user/${userId}`
    );
  }
  deleteAllByUser(userId: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/user/${userId}`);
  }
  countActiveByUser(userId: number): Observable<number> {
    return this.http.get<number>(`${this.url}/user/${userId}/count-active`);
  }
  listActiveByUser(userId: number): Observable<MeetingBooksLinkResponse[]> {
    return this.http.get<MeetingBooksLinkResponse[]>(
      `${this.url}/user/${userId}/active`
    );
  }
  listByType(typeId: number): Observable<MeetingBooksLinkResponse[]> {
    return this.http.get<MeetingBooksLinkResponse[]>(
      `${this.url}/type/${typeId}`
    );
  }
  listByBusiness(businessId: number): Observable<MeetingBooksLinkResponse[]> {
    return this.http.get<MeetingBooksLinkResponse[]>(
      `${this.url}/business/${businessId}`
    );
  }
  /** Público — render de booking page */
  getBySlug(slug: string): Observable<MeetingBooksLinkResponse> {
    return this.http.get<MeetingBooksLinkResponse>(`${this.url}/slug/${slug}`);
  }
  checkSlug(slug: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.url}/check-slug/${slug}`);
  }
  filter(f: BookingLinkFilterParams): Observable<MeetingBooksLinkResponse[]> {
    let params = new HttpParams();
    if (f.userId !== undefined) params = params.set('userId', f.userId.toString());
    if (f.businessId !== undefined)
      params = params.set('businessId', f.businessId.toString());
    if (f.typeId !== undefined) params = params.set('typeId', f.typeId.toString());
    if (f.isActive !== undefined)
      params = params.set('isActive', f.isActive.toString());
    return this.http.get<MeetingBooksLinkResponse[]>(`${this.url}/filter`, {
      params,
    });
  }

  // ---------------------- Sub: Availability ----------------------

  listAvailability(
    bookingLinkId: number
  ): Observable<MeetingBookingAvailabilityResponse[]> {
    return this.http.get<MeetingBookingAvailabilityResponse[]>(
      `${this.url}/${bookingLinkId}/availability`
    );
  }
  listActiveAvailability(
    bookingLinkId: number
  ): Observable<MeetingBookingAvailabilityResponse[]> {
    return this.http.get<MeetingBookingAvailabilityResponse[]>(
      `${this.url}/${bookingLinkId}/availability/active`
    );
  }
  getAvailability(
    bookingLinkId: number,
    id: number
  ): Observable<MeetingBookingAvailabilityResponse> {
    return this.http.get<MeetingBookingAvailabilityResponse>(
      `${this.url}/${bookingLinkId}/availability/${id}`
    );
  }
  createAvailability(
    bookingLinkId: number,
    body: MeetingBookingAvailabilityRequest
  ): Observable<MeetingBookingAvailabilityResponse> {
    return this.http.post<MeetingBookingAvailabilityResponse>(
      `${this.url}/${bookingLinkId}/availability`,
      body
    );
  }
  updateAvailability(
    bookingLinkId: number,
    id: number,
    body: MeetingBookingAvailabilityRequest
  ): Observable<MeetingBookingAvailabilityResponse> {
    return this.http.put<MeetingBookingAvailabilityResponse>(
      `${this.url}/${bookingLinkId}/availability/${id}`,
      body
    );
  }
  deleteAvailability(bookingLinkId: number, id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.url}/${bookingLinkId}/availability/${id}`
    );
  }

  // ---------------------- Sub: Durations ----------------------

  listDurations(
    bookingLinkId: number
  ): Observable<MeetingBookingDurationResponse[]> {
    return this.http.get<MeetingBookingDurationResponse[]>(
      `${this.url}/${bookingLinkId}/durations`
    );
  }
  listActiveDurations(
    bookingLinkId: number
  ): Observable<MeetingBookingDurationResponse[]> {
    return this.http.get<MeetingBookingDurationResponse[]>(
      `${this.url}/${bookingLinkId}/durations/active`
    );
  }
  getDuration(
    bookingLinkId: number,
    id: number
  ): Observable<MeetingBookingDurationResponse> {
    return this.http.get<MeetingBookingDurationResponse>(
      `${this.url}/${bookingLinkId}/durations/${id}`
    );
  }
  createDuration(
    bookingLinkId: number,
    body: MeetingBookingDurationRequest
  ): Observable<MeetingBookingDurationResponse> {
    return this.http.post<MeetingBookingDurationResponse>(
      `${this.url}/${bookingLinkId}/durations`,
      body
    );
  }
  updateDuration(
    bookingLinkId: number,
    id: number,
    body: MeetingBookingDurationRequest
  ): Observable<MeetingBookingDurationResponse> {
    return this.http.put<MeetingBookingDurationResponse>(
      `${this.url}/${bookingLinkId}/durations/${id}`,
      body
    );
  }
  deleteDuration(bookingLinkId: number, id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.url}/${bookingLinkId}/durations/${id}`
    );
  }

  // ---------------------- Sub: Form Fields ----------------------

  listFormFields(
    bookingLinkId: number
  ): Observable<MeetingBookingFormFieldResponse[]> {
    return this.http.get<MeetingBookingFormFieldResponse[]>(
      `${this.url}/${bookingLinkId}/form-fields`
    );
  }
  listActiveFormFields(
    bookingLinkId: number
  ): Observable<MeetingBookingFormFieldResponse[]> {
    return this.http.get<MeetingBookingFormFieldResponse[]>(
      `${this.url}/${bookingLinkId}/form-fields/active`
    );
  }
  getFormField(
    bookingLinkId: number,
    id: number
  ): Observable<MeetingBookingFormFieldResponse> {
    return this.http.get<MeetingBookingFormFieldResponse>(
      `${this.url}/${bookingLinkId}/form-fields/${id}`
    );
  }
  createFormField(
    bookingLinkId: number,
    body: MeetingBookingFormFieldRequest
  ): Observable<MeetingBookingFormFieldResponse> {
    return this.http.post<MeetingBookingFormFieldResponse>(
      `${this.url}/${bookingLinkId}/form-fields`,
      body
    );
  }
  updateFormField(
    bookingLinkId: number,
    id: number,
    body: MeetingBookingFormFieldRequest
  ): Observable<MeetingBookingFormFieldResponse> {
    return this.http.put<MeetingBookingFormFieldResponse>(
      `${this.url}/${bookingLinkId}/form-fields/${id}`,
      body
    );
  }
  deleteFormField(bookingLinkId: number, id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.url}/${bookingLinkId}/form-fields/${id}`
    );
  }

  // ---------------------- Sub: Members ----------------------

  listMembers(
    bookingLinkId: number
  ): Observable<MeetingBookingMemberResponse[]> {
    return this.http.get<MeetingBookingMemberResponse[]>(
      `${this.url}/${bookingLinkId}/members`
    );
  }
  listActiveMembers(
    bookingLinkId: number
  ): Observable<MeetingBookingMemberResponse[]> {
    return this.http.get<MeetingBookingMemberResponse[]>(
      `${this.url}/${bookingLinkId}/members/active`
    );
  }
  getMember(
    bookingLinkId: number,
    id: number
  ): Observable<MeetingBookingMemberResponse> {
    return this.http.get<MeetingBookingMemberResponse>(
      `${this.url}/${bookingLinkId}/members/${id}`
    );
  }
  createMember(
    bookingLinkId: number,
    body: MeetingBookingMemberRequest
  ): Observable<MeetingBookingMemberResponse> {
    return this.http.post<MeetingBookingMemberResponse>(
      `${this.url}/${bookingLinkId}/members`,
      body
    );
  }
  updateMember(
    bookingLinkId: number,
    id: number,
    body: MeetingBookingMemberRequest
  ): Observable<MeetingBookingMemberResponse> {
    return this.http.put<MeetingBookingMemberResponse>(
      `${this.url}/${bookingLinkId}/members/${id}`,
      body
    );
  }
  deleteMember(bookingLinkId: number, id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.url}/${bookingLinkId}/members/${id}`
    );
  }

  // ---------------------- Sub: Reminder Configs ----------------------

  listReminderConfigs(
    bookingLinkId: number
  ): Observable<MeetingReminderConfigResponse[]> {
    return this.http.get<MeetingReminderConfigResponse[]>(
      `${this.url}/${bookingLinkId}/reminder-configs`
    );
  }
  listActiveReminderConfigs(
    bookingLinkId: number
  ): Observable<MeetingReminderConfigResponse[]> {
    return this.http.get<MeetingReminderConfigResponse[]>(
      `${this.url}/${bookingLinkId}/reminder-configs/active`
    );
  }
  getReminderConfig(
    bookingLinkId: number,
    id: number
  ): Observable<MeetingReminderConfigResponse> {
    return this.http.get<MeetingReminderConfigResponse>(
      `${this.url}/${bookingLinkId}/reminder-configs/${id}`
    );
  }
  createReminderConfig(
    bookingLinkId: number,
    body: MeetingReminderConfigRequest
  ): Observable<MeetingReminderConfigResponse> {
    return this.http.post<MeetingReminderConfigResponse>(
      `${this.url}/${bookingLinkId}/reminder-configs`,
      body
    );
  }
  updateReminderConfig(
    bookingLinkId: number,
    id: number,
    body: MeetingReminderConfigRequest
  ): Observable<MeetingReminderConfigResponse> {
    return this.http.put<MeetingReminderConfigResponse>(
      `${this.url}/${bookingLinkId}/reminder-configs/${id}`,
      body
    );
  }
  deleteReminderConfig(bookingLinkId: number, id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.url}/${bookingLinkId}/reminder-configs/${id}`
    );
  }

  // ---------------------- Sub: Nurture Emails ----------------------

  listNurtureEmails(
    bookingLinkId: number
  ): Observable<MeetingNurtureEmailResponse[]> {
    return this.http.get<MeetingNurtureEmailResponse[]>(
      `${this.url}/${bookingLinkId}/nurture-emails`
    );
  }
  listActiveNurtureEmails(
    bookingLinkId: number
  ): Observable<MeetingNurtureEmailResponse[]> {
    return this.http.get<MeetingNurtureEmailResponse[]>(
      `${this.url}/${bookingLinkId}/nurture-emails/active`
    );
  }
  getNurtureEmail(
    bookingLinkId: number,
    id: number
  ): Observable<MeetingNurtureEmailResponse> {
    return this.http.get<MeetingNurtureEmailResponse>(
      `${this.url}/${bookingLinkId}/nurture-emails/${id}`
    );
  }
  createNurtureEmail(
    bookingLinkId: number,
    body: MeetingNurtureEmailRequest
  ): Observable<MeetingNurtureEmailResponse> {
    return this.http.post<MeetingNurtureEmailResponse>(
      `${this.url}/${bookingLinkId}/nurture-emails`,
      body
    );
  }
  updateNurtureEmail(
    bookingLinkId: number,
    id: number,
    body: MeetingNurtureEmailRequest
  ): Observable<MeetingNurtureEmailResponse> {
    return this.http.put<MeetingNurtureEmailResponse>(
      `${this.url}/${bookingLinkId}/nurture-emails/${id}`,
      body
    );
  }
  deleteNurtureEmail(bookingLinkId: number, id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.url}/${bookingLinkId}/nurture-emails/${id}`
    );
  }

  // ---------------------- Sub: Round-Robin State ----------------------

  listRoundRobinState(
    bookingLinkId: number
  ): Observable<MeetingRoundRobinStateResponse[]> {
    return this.http.get<MeetingRoundRobinStateResponse[]>(
      `${this.url}/${bookingLinkId}/round-robin-state`
    );
  }
  nextRoundRobinUser(
    bookingLinkId: number
  ): Observable<MeetingRoundRobinStateResponse> {
    return this.http.get<MeetingRoundRobinStateResponse>(
      `${this.url}/${bookingLinkId}/round-robin-state/next`
    );
  }
  resetRoundRobinState(bookingLinkId: number): Observable<void> {
    return this.http.post<void>(
      `${this.url}/${bookingLinkId}/round-robin-state/reset`,
      null
    );
  }
  assignRoundRobinUser(
    bookingLinkId: number,
    idUser: number
  ): Observable<MeetingRoundRobinStateResponse> {
    const params = new HttpParams().set('idUser', idUser.toString());
    return this.http.post<MeetingRoundRobinStateResponse>(
      `${this.url}/${bookingLinkId}/round-robin-state/assign`,
      null,
      { params }
    );
  }
}
